import * as React from 'react';
import {
  ComparisonType,
  DashboardCurrency,
  DashboardDateRange,
  DashboardGranularity,
  DashboardGraphParameters,
  DashboardQueryParameters,
  DashboardDataResults,
} from '../entities';
import { timeNow } from '../utils/dates';
import { getComparisonFromToUrlSegment, getFromToUrlSegment, getQueryString } from '../utils/mapping';
import { IValue as ImportedValue, ReportableDataProvider } from './ReportableDataProvider';
import { ActionTypes, ReportableDataReducer } from './ReportableDataReducer';

export type IValue = ImportedValue;

export interface IState {
  loading: boolean;
  queryString: string;
  dateTarget: string;
  comparisonDateTarget: string;
  graphParameters: DashboardGraphParameters;
  queryParameters: DashboardQueryParameters;
  currencies?: DashboardCurrency[];
  data?: DashboardDataResults;
}

export interface IAction {
  type: ActionTypes;
  payload?: any;
}

export type Dispatch = (action: IAction) => void;

export const defaultContextValues: IState = {
  loading: true,
  queryParameters: {
    filterCurrency: null,
    dateRange: DashboardDateRange.TODAY,
    dateRangeOffset: 0,
    comparisonType: ComparisonType.NONE,
    comparisonDateRange: DashboardDateRange.TODAY,
    comparisonDateRangeOffset: 0,
    siteIds: [],
  },
  graphParameters: {
    baseCurrency: DashboardCurrency.GBP,
    comparisonEnabled: false,
    granularity: DashboardGranularity.DAY,
    timezone: 'Europe/London',
  },
  currencies: [DashboardCurrency.GBP],
  dateTarget: '',
  comparisonDateTarget: '',
  queryString: '',
  data: {
    salesTotals: {
      DirectCount: 0,
      DirectValue: 0,
      RecoveredCount: 0,
      RecoveredValue: 0,
    },
    comparisonSalesTotals: {
      DirectCount: 0,
      DirectValue: 0,
      RecoveredCount: 0,
      RecoveredValue: 0,
    },
  },
};

export function setInititalContext(timezone: string): IState {
  const state = defaultContextValues;
  state.graphParameters.timezone = timezone;
  const timezoneMoment = timeNow(timezone);
  state.dateTarget = getFromToUrlSegment(state.queryParameters, timezoneMoment);
  state.comparisonDateTarget = getComparisonFromToUrlSegment(state.queryParameters, timezoneMoment);
  state.queryString = getQueryString(state.queryParameters, state.graphParameters);
  return state;
}

export const ReportableDataStateContext = React.createContext<IState | undefined>(undefined);
export const ReportableDataDispatchContext = React.createContext<Dispatch | undefined>(undefined);

function useReportableDataState() {
  const context = React.useContext(ReportableDataStateContext);
  if (context === undefined) {
    throw new Error('useReportableDataState must be used within ReportableDataProvider');
  }
  return context;
}

function useReportableDataDispatch() {
  const context = React.useContext(ReportableDataDispatchContext);
  if (context === undefined) {
    throw new Error('useReportableDataDispatch must be used within a ReportableDataProvider');
  }
  return context;
}

export { ReportableDataProvider, ReportableDataReducer, useReportableDataState, useReportableDataDispatch };
